import { useMemo } from "react";
import type { RestaurantBooking } from "./types";
import { businessDateOf, currentBusinessDate } from "./lib/business-day";

function dayLabel(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
}

/** Richieste ancora da gestire: solo da oggi in poi. Quelle dei giorni passati mai confermate sono scadute
 *  e non devono gonfiare il contatore (caso Narciso 28/10/2026: «30 in attesa» ma lista vuota). */
export function upcomingPending(bookings: RestaurantBooking[]) {
  const today = currentBusinessDate();
  return bookings
    .filter((booking) => booking.status === "pending" && businessDateOf(booking.date, booking.time) >= today)
    .sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));
}

export default function PendingRequests({ bookings, canEdit, busyId, selectedDate, onConfirm, onReject, onGoToDay, onClose }: {
  bookings: RestaurantBooking[];
  canEdit: boolean;
  busyId: string;
  selectedDate: string;
  onConfirm: (booking: RestaurantBooking) => void;
  onReject: (booking: RestaurantBooking) => void;
  onGoToDay: (booking: RestaurantBooking) => void;
  onClose: () => void;
}) {
  const upcoming = useMemo(() => upcomingPending(bookings), [bookings]);

  return <section className="pending-panel" aria-label="Richieste da confermare">
    <header>
      <div><h3>Richieste da confermare</h3><p>{upcoming.length === 0 ? "Tutto gestito." : `${upcoming.length} ${upcoming.length === 1 ? "richiesta in attesa" : "richieste in attesa"}, di tutti i giorni. Confermale da qui o aprine il giorno.`}</p></div>
      <button type="button" onClick={onClose}>Chiudi</button>
    </header>
    {upcoming.map((booking) => {
      const sameDay = businessDateOf(booking.date, booking.time) === selectedDate;
      return <article key={booking.id} className={sameDay ? "is-today" : ""}>
        <time dateTime={`${booking.date}T${booking.time}`}><strong>{new Date(`${booking.date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</strong><span>{booking.time}</span></time>
        <div className="pending-info">
          <strong>{booking.customer.firstName} {booking.customer.lastName}</strong>
          <span>{booking.partySize} {booking.partySize === 1 ? "persona" : "persone"} · {booking.serviceName} · <a href={`tel:${booking.customer.phone}`}>{booking.customer.phone}</a></span>
          <small>{dayLabel(booking.date)}{booking.customer.email ? ` · ${booking.customer.email}` : ""}</small>
          {(booking.details.occasion || booking.details.notes) && <p>{[booking.details.occasion, booking.details.notes].filter(Boolean).join(" · ")}</p>}
        </div>
        <div className="pending-actions">
          <button type="button" className="pending-confirm" disabled={!canEdit || busyId === booking.id} onClick={() => onConfirm(booking)}>{busyId === booking.id ? "Confermo…" : "Conferma"}</button>
          <button type="button" disabled={!canEdit || busyId === booking.id} onClick={() => onGoToDay(booking)}>{sameDay ? "Vedi nell'agenda" : "Vai al giorno"}</button>
          <button type="button" className="pending-reject" disabled={!canEdit || busyId === booking.id} onClick={() => onReject(booking)}>Rifiuta</button>
        </div>
      </article>;
    })}
  </section>;
}
